/**
 * Clean a stored person/lead name for display.
 *
 * 5,849 of 287,932 leads (2.0%) have a literal "None" token sitting inside the
 * `name` column — names assembled by joining a first and last name where one side
 * was a Python `None`, so the stored string itself reads "None None", "Praveen None"
 * or "None mahender kumar". They render that way in the leads table, lead detail,
 * the WhatsApp inbox and anywhere else a customer is named.
 *
 * This is a DISPLAY fix, not a repair: the rows are untouched, so nothing is lost
 * and the real backfill can still happen later. It lives here rather than in one
 * screen because the same bad names surface on all of them.
 *
 * Only whole-word "None" is stripped, so genuine names survive:
 *   "Noney Sharma" -> "Noney Sharma"      (not a standalone token)
 *   "Anne None"    -> "Anne"
 *   "Praveen None" -> "Praveen"
 *   "None None"    -> ''                  (nothing real left)
 *   ". None"       -> ''                  (punctuation only — no letters or digits)
 *
 * Returns '' when nothing usable remains, so callers keep their own fallback
 * (`displayName(lead.name) || lead.phone || 'Unknown'`) and never print a stray
 * fragment.
 */
const NONE_TOKEN = /(?:^|\s)None(?=\s|$)/g;

export const displayName = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const cleaned = raw.replace(NONE_TOKEN, ' ').trim().replace(/\s+/g, ' ');
  // A name made only of punctuation (". None" -> ".") is noise, not a name.
  return /[\p{L}\p{N}]/u.test(cleaned) ? cleaned : '';
};

export default displayName;
